/**
 * Header Dropdown Color Manager
 * Tracks when dropdown/megamenu is opened and changes header elements colors
 */

class HeaderDropdownManager {
  constructor() {
    this.header = null;
    this.dropdownItems = [];
    this.megaMenus = [];
    this.isDropdownOpen = false;
    this.openDropdown = null;
    this.hoverTimeout = null;
    
    this.init();
  }
  
  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }
  
  setup() {
    // Get header element
    this.header = document.querySelector('.header, #shopify-section-header .header');
    if (!this.header) {
      console.warn('Header element not found');
      return;
    }
    
    // Find all dropdown triggers and menus
    this.findDropdownElements();
    
    // Set up event listeners
    this.attachEventListeners();
  }
  
  findDropdownElements() {
    // Common selectors for dropdown menu items
    const dropdownSelectors = [
      '.header__menu-item--top',
      '.list-menu__item--link',
      '[data-dropdown]',
      '[aria-haspopup="true"]',
      '.menu-item-has-children',
      '.header__menu-item',
      '.desktop-menu .list-menu__item',
      '.desktop-menu > ul > li',
      'nav > ul > li',
      '.header-menu > ul > li',
      '.main-menu > ul > li'
    ];
    
    // Common selectors for mega menus / dropdowns
    const megaMenuSelectors = [
      '.mega-menu',
      '.dropdown-menu', 
      '.submenu',
      '[data-dropdown-menu]',
      '.list-menu--disclosure',
      '.header-submenu',
      '.header-dropdown',
      'ul ul', // nested lists
      '.dropdown-content',
      '.menu-dropdown'
    ];
    
    // Find dropdown triggers
    dropdownSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        if (!this.dropdownItems.includes(el)) {
          this.dropdownItems.push(el);
        }
      });
    });
    
    // Find mega menus
    megaMenuSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        if (!this.megaMenus.includes(el)) {
          this.megaMenus.push(el);
        }
      });
    });
    
    // Also look for any element with dropdown in class name or data attributes
    const allElements = document.querySelectorAll('*[class*="dropdown"], *[class*="menu"], *[data-menu], *[data-dropdown]');
    allElements.forEach(el => {
      const isInHeader = el.closest('.header');
      if (isInHeader && !this.dropdownItems.includes(el) && !this.megaMenus.includes(el)) {
        // Determine if it's a trigger or menu
        if (el.getAttribute('aria-haspopup') || el.querySelector('ul') || el.querySelector('.submenu')) {
          this.dropdownItems.push(el);
        } else if (el.tagName === 'UL' || el.classList.contains('submenu') || el.classList.contains('dropdown')) {
          this.megaMenus.push(el);
        }
      }
    });
  }
  
  attachEventListeners() {
    // Add hover listeners to dropdown items - but only check for actual dropdown visibility
    this.dropdownItems.forEach(item => {
      item.addEventListener('mouseenter', (e) => this.handleDropdownHover(e));
      item.addEventListener('mouseleave', (e) => this.handleDropdownLeave(e));
    });
    
    // Add listeners to mega menus themselves
    this.megaMenus.forEach(menu => {
      menu.addEventListener('mouseenter', () => this.setDropdownState(true));
      menu.addEventListener('mouseleave', () => this.setDropdownState(false));
    });
    
    // Listen for clicks that might toggle dropdowns
    document.addEventListener('click', (e) => {
      const dropdownTrigger = e.target.closest('[data-dropdown], [aria-haspopup="true"], .header__menu-item--top');
      if (dropdownTrigger) {
        // Check dropdown state after click
        setTimeout(() => this.checkDropdownVisibility(), 50);
      }
    });
    
    // Listen for escape key to close dropdowns
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.setDropdownState(false);
      }
    });
    
    // Monitor for dynamically created dropdowns
    this.observeDropdowns();
    
    // Continuous monitoring for dropdown visibility changes
    this.startDropdownMonitoring();
  }
  
  handleDropdownHover(event) {
    // Only check for actual dropdown visibility, don't immediately set state
    clearTimeout(this.hoverTimeout);
    
    this.hoverTimeout = setTimeout(() => {
      this.checkDropdownVisibility();
    }, 150); // Give time for dropdown to appear
  }
  
  handleDropdownLeave(event) {
    clearTimeout(this.hoverTimeout);
    
    // Delay checking to allow moving to dropdown content
    this.hoverTimeout = setTimeout(() => {
      this.checkDropdownVisibility();
    }, 200);
  }
  
  startDropdownMonitoring() {
    // Check dropdown visibility periodically when hovering over header
    let monitorInterval = null;
    
    this.header.addEventListener('mouseenter', () => {
      monitorInterval = setInterval(() => {
        this.checkDropdownVisibility();
      }, 100);
    });
    
    this.header.addEventListener('mouseleave', () => {
      if (monitorInterval) {
        clearInterval(monitorInterval);
        monitorInterval = null;
      }
      // Final check after leaving header
      setTimeout(() => this.checkDropdownVisibility(), 200);
    });
  }
  
  checkDropdownVisibility() {
    // Check if any dropdown/megamenu is actually visible
    const hasVisibleDropdown = this.megaMenus.some(menu => {
      const styles = window.getComputedStyle(menu);
      return styles.display !== 'none' && styles.visibility !== 'hidden' && styles.opacity !== '0';
    });
    
    this.setDropdownState(hasVisibleDropdown);
  }
  
  setDropdownState(isOpen, triggerElement = null) {
    if (this.isDropdownOpen === isOpen) return;
    
    this.isDropdownOpen = isOpen;
    this.openDropdown = isOpen ? triggerElement : null;
    
    if (isOpen) {
      this.header.classList.add('dropdown-open');
      this.header.classList.add('is-active');
    } else {
      this.header.classList.remove('dropdown-open');
      this.header.classList.remove('is-active');
    }
    
    // Dispatch custom event for other scripts to listen to
    const event = new CustomEvent('headerDropdownChange', {
      detail: { 
        isOpen, 
        triggerElement,
        header: this.header 
      }
    });
    document.dispatchEvent(event);
  }
  
  observeDropdowns() {
    // Use MutationObserver to watch for dynamically added dropdown content
    const observer = new MutationObserver((mutations) => {
      let shouldRecheck = false;
      
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            const hasDropdownClass = node.className && (
              node.className.includes('dropdown') || 
              node.className.includes('submenu') ||
              node.className.includes('mega-menu')
            );
            
            if (hasDropdownClass) {
              shouldRecheck = true;
            }
          }
        });
      });
      
      if (shouldRecheck) {
        setTimeout(() => this.checkDropdownVisibility(), 100);
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });
  }
  
  // Public methods for external control
  forceDropdownState(isOpen) {
    this.setDropdownState(isOpen);
  }
  
  getCurrentState() {
    return {
      isDropdownOpen: this.isDropdownOpen,
      openDropdown: this.openDropdown
    };
  }
}

// Initialize the header dropdown manager
let headerDropdownManager;

// Auto-initialize
if (typeof window !== 'undefined') {
  headerDropdownManager = new HeaderDropdownManager();
  
  // Make it globally accessible
  window.HeaderDropdownManager = HeaderDropdownManager;
  window.headerDropdownManager = headerDropdownManager;
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = HeaderDropdownManager;
}
